import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Check, Headphones, HelpCircle, RotateCcw, Speaker, Star, X } from "lucide-react";
import type { ChildProfile, ContentNode } from "../../types";

interface ChildLessonProps {
  child: ChildProfile;
  contentNode: ContentNode;
  onAttempt: (correct: boolean, hints: number) => void;
}

const stepLabels = ["Teach", "Practise", "Try", "Review", "Home"];

export function ChildLesson({ child, contentNode, onAttempt }: ChildLessonProps) {
  const [step, setStep] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [hintCount, setHintCount] = useState(0);
  const [feedback, setFeedback] = useState("Listen, try, then check.");

  const activity = useMemo(() => buildActivity(contentNode), [contentNode]);

  function submitAnswer(answer: string) {
    const correct = answer === activity.correctAnswer;
    setSelectedAnswer(answer);
    setFeedback(correct ? "That is secure. We will see it again later." : "Good try. Let us use a teach card and try once more.");
    onAttempt(correct, hintCount);
    setStep((current) => Math.min(current + 1, stepLabels.length - 1));
  }

  function resetLesson() {
    setStep(0);
    setSelectedAnswer(null);
    setHintCount(0);
    setFeedback("Listen, try, then check.");
  }

  return (
    <div className="child-lesson-layout">
      <section className="surface-panel lesson-panel" aria-labelledby="lesson-title">
        <div className="lesson-topline">
          <div>
            <span className="small-label">{contentNode.subject} - {contentNode.strand}</span>
            <h3 id="lesson-title">{activity.title}</h3>
          </div>
          <div className="reward-counter" aria-label="Review points">
            <Star size={18} fill="currentColor" aria-hidden="true" />
            320
          </div>
        </div>

        <div className="stepper" aria-label="Lesson progress">
          {stepLabels.map((label, index) => (
            <span className={index <= step ? "step-dot is-active" : "step-dot"} key={label}>
              <span className="visually-hidden">{label}</span>
            </span>
          ))}
        </div>

        <article className="lesson-card">
          <div className="lesson-visual" aria-hidden="true">
            <span className="lesson-sun" />
            <span className="lesson-boat" />
            <span className="lesson-wave" />
          </div>
          <span className="small-label">{stepLabels[step]}</span>
          <h4>{activity.prompt}</h4>
          <p>{contentNode.instructionScript}</p>
          <div className="answer-grid" role="list" aria-label="Answer choices">
            {activity.answers.map((answer) => {
              const isSelected = selectedAnswer === answer;
              const correct = answer === activity.correctAnswer;
              return (
                <button
                  className={answerClass(isSelected, correct)}
                  disabled={selectedAnswer !== null}
                  key={answer}
                  onClick={() => submitAnswer(answer)}
                  type="button"
                >
                  {isSelected && correct ? <Check size={18} aria-hidden="true" /> : null}
                  {isSelected && !correct ? <X size={18} aria-hidden="true" /> : null}
                  {answer}
                </button>
              );
            })}
          </div>
        </article>

        <div className="lesson-controls">
          <button
            className="secondary-button"
            onClick={() => {
              setHintCount((current) => current + 1);
              setFeedback(activity.hint);
            }}
            type="button"
          >
            <HelpCircle size={18} aria-hidden="true" />
            Get help
          </button>
          <button className="secondary-button" onClick={resetLesson} type="button">
            <RotateCcw size={18} aria-hidden="true" />
            Restart
          </button>
          <button
            className="primary-button"
            onClick={() => setStep((current) => Math.min(current + 1, stepLabels.length - 1))}
            type="button"
          >
            Next
          </button>
        </div>
        <p className="lesson-feedback" aria-live="polite">{feedback}</p>
      </section>

      <aside className="surface-panel support-panel" aria-labelledby="support-title">
        <div className="panel-heading">
          <h3 id="support-title">{child.name}'s supports</h3>
          <span>Learning supports</span>
        </div>
        <div className="support-list">
          <SupportItem icon={<Speaker size={18} />} label="Narration" value="British English" />
          <SupportItem icon={<Headphones size={18} />} label="Replay" value="Available" />
          <SupportItem icon={<Check size={18} />} label="Reduced motion" value="Respected" />
        </div>
        <div className="offscreen-card">
          <strong>Off-screen card</strong>
          <p>{contentNode.parentExtension}</p>
        </div>
      </aside>
    </div>
  );
}

function SupportItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <article>
      <span className="mini-icon" aria-hidden="true">{icon}</span>
      <div>
        <strong>{label}</strong>
        <p>{value}</p>
      </div>
    </article>
  );
}

function answerClass(selected: boolean, correct: boolean) {
  if (!selected) return "answer-button";
  return correct ? "answer-button is-correct" : "answer-button is-wrong";
}

function buildActivity(contentNode: ContentNode) {
  if (contentNode.subject === "Maths") {
    return {
      title: "Build the amount carefully.",
      prompt: contentNode.skill === "ratio-table" ? "Which scaled pair keeps the same ratio?" : "Which two parts make six?",
      answers: contentNode.skill === "ratio-table" ? ["4:9", "10:16", "7:8", "5:10"] : ["3 and 3", "4 and 4", "1 and 4", "2 and 5"],
      correctAnswer: contentNode.skill === "ratio-table" ? "10:16" : "3 and 3",
      hint: contentNode.skill === "ratio-table" ? "Use the same multiplier on both sides." : "Keep the total at six. Try counting both parts together."
    };
  }

  return {
    title: "Read the word.",
    prompt: contentNode.skill === "multi-syllable" ? "Where would you split the word?" : "Which word matches the sound?",
    answers: contentNode.skill === "multi-syllable" ? ["en-vi-ron-men-tal", "env-i-ronm-ental", "environ-ment-al", "environment-al"] : ["boat", "boot", "bat", "bait"],
    correctAnswer: contentNode.skill === "multi-syllable" ? "en-vi-ron-men-tal" : "boat",
    hint: contentNode.skill === "multi-syllable" ? "Look for chunks you can say clearly before blending the whole word." : "Look at every letter pattern before you blend."
  };
}
